import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { GridColumn } from '@progress/kendo-react-grid';
import {
  ContextMenu,
  MenuItem,
  MenuSelectEvent,
} from '@progress/kendo-react-layout';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { plusIcon } from '@progress/kendo-svg-icons';

import { ILocationsProps } from './Locations.types';

import { Button, ListingGrid } from '../../common';
import LocationsDialog from './LocationsDialog';

import { columns } from './model';
import { ILocation } from '../../../data/types';

import {
  useCreateLocation,
  useDeleteLocationById,
  useUpdateLocation,
} from '../../../services/hooks/useLocations';

import {
  FormContainer,
  Form,
  ListingGridContainer,
  ButtonWrapper,
} from './Locations.styled';
import { CustomGrid } from '../../../themes/common.styled';
import CloseButton from '../../common/CloseButton';
import { useKeyPress } from '../../../hooks';
import PageNavButton from '../../common/PageNavButton';

type SelectionType = { field?: string; dataItem: any };

const Locations: FC<ILocationsProps> = ({ title, resultData, onClose, onNextPage, onPreviousPage }) => {
  const [originData, setOriginData] = useState<ILocation[]>(resultData[0].data);
  const [filterData, setFilterData] = useState<ILocation[]>(resultData[0].data);
  const [showContext, setShowContext] = useState<boolean>(false);
  const [sort, setSort] = useState<SortDescriptor[]>();
  const [selectedItem, setSelectedItem] = useState<SelectionType>();
  const [showDialog, setShowDialog] = useState<{
    show: boolean;
    isEdit: boolean;
    code: string;
  }>({
    show: false,
    isEdit: false,
    code: '',
  });

  const offset = useRef({ left: 0, top: 0 });

  useEffect(() => {
    setOriginData(resultData[0].data);
    setFilterData(resultData[0].data);
  }, [resultData]);

  const refetchResults = () => {
    resultData.forEach((result) => result.refetch());
  };

  const handleOpenDialog = (isEdit: boolean, code: string) => {
    setShowDialog({
      show: true,
      isEdit,
      code,
    });
  };

  const handleCloseDialog = (shouldRefetch?: boolean) => {
    setShowDialog({
      show: false,
      isEdit: false,
      code: '',
    });
    if (shouldRefetch) {
      setTimeout(() => {
        refetchResults();
      }, 1000);
    }
  };

  const { mutate: updateLocation } = useUpdateLocation(handleCloseDialog);
  const { mutate: createLocation } = useCreateLocation(handleCloseDialog);
  const { mutate: deleteLocation } = useDeleteLocationById(handleCloseDialog);

  const handleSubmitLocation = (params: { isNew: boolean; data: any }) => {
    const { isNew, data } = params;
    try {
      if (isNew) {
        createLocation({
          formattedData: data,
        });
      } else {
        updateLocation({
          formattedData: data,
        });
      }
    } catch (e: unknown) {
      alert(`error while saving data: ${String(e)}`);
    }
  };

  const handleDeleteLocation = (params: { id: any }) => {
    const { id } = params;

    try {
      deleteLocation({ id });
    } catch (e: unknown) {
      alert(`error while saving data: ${String(e)}`);
    }
  };

  const handleContextMenuOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    offset.current = {
      left: e.pageX,
      top: e.pageY,
    };
    setShowContext(true);
  };

  const handleOnSelect = useCallback(
    (e: MenuSelectEvent) => {
      if (selectedItem) {
        const { field, dataItem } = selectedItem;
        switch (e.item.data.action) {
          case 'sortAsc':
            setSort([
              {
                dir: 'asc',
                field: field ?? '',
              },
            ]);
            break;
          case 'sortDesc':
            setSort([
              {
                dir: 'desc',
                field: field ?? '',
              },
            ]);
            break;
          case 'filterIn':
            if (field) {
              setFilterData(
                originData.filter(
                  (data: any) => data[field] === dataItem[field]
                )
              );
            }
            break;
          case 'filterOut':
            if (field) {
              setFilterData(
                originData.filter(
                  (data: any) => data[field] !== dataItem[field]
                )
              );
            }
            break;
          case 'removeFilters':
            setFilterData(originData);
            break;
          default:
        }
      }
      setShowContext(false);
    },
    [selectedItem, originData]
  );

  useKeyPress(
    () => {
    },
    () => {
    },
    () => {
    },
    () => {
      handleOpenDialog(false, '');
    }
  );

  return (
    <>
      <FormContainer>
        <Form>
          <div className="form-section-header-container">
            <div className="form-title">{title}</div>
            <div className="form-header-btn-wrapper">
              <PageNavButton
                className="listing-action-button"
                onClick={(e) => {
                  e.preventDefault();
                  onPreviousPage();
                }}
                title="Previous Page"
              >
                <span
                  className="k-icon k-i-arrow-left k-button-icon"
                  role="presentation"
                />
              </PageNavButton>
              <PageNavButton
                className="listing-action-button"
                onClick={(e) => {
                  e.preventDefault();
                  onNextPage();
                }}
                title="Next Page"
              >
                <span
                  className="k-icon k-i-arrow-right k-button-icon"
                  role="presentation"
                />
              </PageNavButton>
              <CloseButton
                className="listing-action-button"
                onClick={onClose}
                title="Close"
              >
                <span
                  className="k-icon k-i-close k-button-icon"
                  role="presentation"
                />
              </CloseButton>
            </div>
          </div>
          <div className="form-content">
            <CustomGrid
              cols="800px"
              colGap="0px"
              width="auto"
              style={{}}
            >
              <ListingGridContainer>
                <ListingGrid
                  data={sort ? orderBy(filterData, sort) : filterData}
                  dataItemKey="LocCode"
                  onRowClick={(e) => {
                    setFilterData((prev) =>
                      prev.map((item) => ({
                        ...item,
                        selected: item.LocCode === e.dataItem.LocCode,
                      }))
                    );
                  }}
                  onRowDoubleClick={(e) => {
                    handleOpenDialog(true, e.dataItem?.LocCode);
                  }}
                  sortable={true}
                  sort={sort}
                  onSortChange={(e) => {
                    setSort(e.sort);
                  }}
                  onContextMenu={(event) => {
                    handleContextMenuOpen(event.syntheticEvent);
                    const { dataItem, field } = event;
                    setFilterData((prev) =>
                      prev.map((item) => ({
                        ...item,
                        selected: item.LocCode === dataItem.LocCode,
                      }))
                    );
                    setSelectedItem({ field, dataItem });
                  }}
                >
                  {columns.map((column, index) => (
                    <GridColumn {...column} key={index} />
                  ))}
                </ListingGrid>
                <ContextMenu
                  show={showContext}
                  offset={offset.current}
                  onSelect={handleOnSelect}
                  onClose={() => setShowContext(false)}
                  className="context-menu"
                >
                  <MenuItem
                    text="Sort Ascending"
                    data={{
                      action: 'sortAsc',
                    }}
                  />
                  <MenuItem
                    text="Sort Descending"
                    data={{
                      action: 'sortDesc',
                    }}
                  />
                  <MenuItem
                    text="Filter In Selected"
                    data={{
                      action: 'filterIn',
                    }}
                  />
                  <MenuItem
                    text="Filter Out Selected"
                    data={{
                      action: 'filterOut',
                    }}
                  />
                  <MenuItem
                    text="Remove All Filters"
                    data={{
                      action: 'removeFilters',
                    }}
                  />
                </ContextMenu>
              </ListingGridContainer>
              <ButtonWrapper>
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenDialog(false, '');
                  }}
                  id="create-btn"
                  title="Add New"
                  svgIcon={plusIcon}
                >
                  ADD <u>N</u>EW
                </Button>
              </ButtonWrapper>
            </CustomGrid>
          </div>
        </Form>
      </FormContainer>
      {showDialog.show && (
        <LocationsDialog
          isEdit={showDialog.isEdit}
          id={showDialog.code}
          handleClose={handleCloseDialog}
          handleSubmitLocation={handleSubmitLocation}
          handleDeleteLocation={handleDeleteLocation}
        />
      )}
    </>
  );
};

export default Locations;
